import { AttachmentCategory, type AttachmentSelectDto, AttachmentType } from '@@types/api.types';
import { useSyncBufferedAnswerAttachments } from '@components/pages/survey/hooks/use-sync-buffered-answer-attachments.hook';
import { AttachmentsList } from '@components/shared/attachments/attachments-list.component';
import { AttachmentsModal } from '@components/shared/attachments/attachments-modal.component';
import { BufferedAttachmentsList } from '@components/shared/attachments/buffered-attachments-list.component';
import { FormLabel } from '@components/shared/form-label.component';
import { Button } from '@components/ui/button';
import { useInnovatorGetAnswerAttachments } from '@queries/attachments.query';
import type { BufferedAttachment } from '@queries/attachments.query';
import { useSurveyAttachmentsStore } from '@stores/survey-attachments.store';
import { type FunctionComponent, type MouseEvent, useEffect, useState } from 'react';

type AnswerAttachmentsInputProps = {
  answerId?: string;
  canEdit: boolean;
  onModalOpenChange?: (isOpen: boolean) => void;
  questionAttachmentKey?: string;
};

type ModalState =
  | { status: 'closed' }
  | { status: 'creating' }
  | { status: 'editing-buffered'; attachment: BufferedAttachment }
  | { status: 'editing-live'; attachment: AttachmentSelectDto };

const CLOSED_MODAL: ModalState = { status: 'closed' };
const EMPTY_BUFFERED_ATTACHMENTS: BufferedAttachment[] = [];

export const AnswerAttachmentsInput: FunctionComponent<AnswerAttachmentsInputProps> = (props) => {
  const { answerId, canEdit, onModalOpenChange, questionAttachmentKey } = props;

  const bufferedAttachments = useSurveyAttachmentsStore((state) => {
    if (!questionAttachmentKey) {
      return EMPTY_BUFFERED_ATTACHMENTS;
    }

    return state.bufferedByQuestion[questionAttachmentKey] ?? EMPTY_BUFFERED_ATTACHMENTS;
  });
  const updateBufferedAttachments = useSurveyAttachmentsStore((state) => state.updateBufferedAttachments);

  const { data: answerAttachments = [] } = useInnovatorGetAnswerAttachments({
    answerId: answerId ?? '',
    category: AttachmentCategory.GENERAL,
  });

  const [modalState, setModalState] = useState<ModalState>(CLOSED_MODAL);
  const { isSyncingBufferedAttachments } = useSyncBufferedAnswerAttachments({
    answerId,
    bufferedAttachments,
    canEdit,
    questionAttachmentKey,
  });

  const isBufferMode = !answerId;
  const bufferedCount = bufferedAttachments.length;
  const hasBufferedAttachments = bufferedCount > 0;
  const hasLiveAttachments = answerAttachments.length > 0;
  const totalCount = hasBufferedAttachments ? bufferedCount : answerAttachments.length;

  // --- Buffer handlers ---

  const handleBufferSave = (data: BufferedAttachment): void => {
    if (!questionAttachmentKey) return;

    if (modalState.status === 'editing-buffered') {
      updateBufferedAttachments({
        attachment: data,
        clientId: modalState.attachment.clientId,
        questionAttachmentKey,
      });
      return;
    }

    const clientId = data.clientId ?? crypto.randomUUID();
    updateBufferedAttachments({
      attachments: [...bufferedAttachments, { ...data, clientId }],
      questionAttachmentKey,
    });
  };

  const handleBufferRemove = (clientId: string): void => {
    if (!questionAttachmentKey) return;

    updateBufferedAttachments({
      attachments: bufferedAttachments.filter((a) => a.clientId !== clientId),
      questionAttachmentKey,
    });
  };

  // --- Modal handlers ---

  const handleCloseModal = (): void => {
    setModalState(CLOSED_MODAL);
  };

  const handleOpenModal = (attachment?: AttachmentSelectDto): void => {
    setModalState(attachment ? { attachment, status: 'editing-live' } : { status: 'creating' });
  };

  const handleOpenBufferedModal = (attachment?: BufferedAttachment): void => {
    setModalState(attachment ? { attachment, status: 'editing-buffered' } : { status: 'creating' });
  };

  const handleAddAttachmentClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    if (isBufferMode) {
      handleOpenBufferedModal();
      return;
    }

    handleOpenModal();
  };

  // --- Derived modal props ---

  const isModalOpen = modalState.status !== 'closed';
  const attachmentToEdit = modalState.status === 'editing-live' ? modalState.attachment : null;
  const bufferedAttachmentToEdit = modalState.status === 'editing-buffered' ? modalState.attachment : null;

  useEffect(() => {
    onModalOpenChange?.(isModalOpen);

    return () => {
      onModalOpenChange?.(false);
    };
  }, [isModalOpen, onModalOpenChange]);

  // --- Render helpers ---

  const renderAttachmentsList = (): React.ReactNode => {
    if (hasBufferedAttachments) {
      return (
        <BufferedAttachmentsList
          attachments={bufferedAttachments}
          onEdit={canEdit ? handleOpenBufferedModal : undefined}
          onRemove={canEdit ? handleBufferRemove : undefined}
        />
      );
    }

    if (isBufferMode) return null;

    if (hasLiveAttachments) {
      return (
        <AttachmentsList
          attachments={answerAttachments}
          onEdit={canEdit ? handleOpenModal : undefined}
          showHeader={false}
          useFormLabel={false}
        />
      );
    }

    return null;
  };

  return (
    <section className="mt-4 flex w-full flex-col gap-2">
      <div className="flex flex-col items-start">
        <FormLabel label={`Attachments (${totalCount})`} />
      </div>

      <div className="min-h-0">{renderAttachmentsList()}</div>

      {canEdit && (
        <Button
          className="self-start"
          disabled={isSyncingBufferedAttachments}
          onClick={handleAddAttachmentClick}
          variant="link"
        >
          + Add Attachment
        </Button>
      )}

      <AttachmentsModal
        attachmentToEdit={attachmentToEdit}
        bufferedAttachmentToEdit={bufferedAttachmentToEdit}
        category={AttachmentCategory.GENERAL}
        defaultType={AttachmentType.FILE}
        isOpen={isModalOpen}
        onBufferRemove={isBufferMode ? handleBufferRemove : undefined}
        onBufferSave={isBufferMode ? handleBufferSave : undefined}
        onClose={handleCloseModal}
        targetId={answerId}
        targetType="answer"
      />
    </section>
  );
};
